// src/pages/ReturnsPage.jsx
import React from "react";
import { Link, useNavigate } from "react-router-dom";

export default function ReturnsPage() {
  const navigate = useNavigate();

  return (
    <div className="rt-root">
      <style dangerouslySetInnerHTML={{ __html: STYLES }} />

      {/* HERO */}
      <section className="rt-hero">
        <button className="rt-back" onClick={() => navigate(-1)}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <path d="M19 12H5" /><path d="M12 19l-7-7 7-7" />
          </svg>
          Back
        </button>
        <div className="rt-badge">Returns & Refunds</div>
        <h1>Easy <span className="grad">Returns</span></h1>
        <p className="rt-lede">
          If you're not satisfied, return most items within 30 days for a full refund.
        </p>
      </section>

      {/* KEY PROMISES */}
      <section className="rt-promises">
        <div className="rt-promise">
          <div className="rt-promise-icon">⏱️</div>
          <div className="rt-promise-n">30 days</div>
          <div className="rt-promise-l">From delivery date</div>
        </div>
        <div className="rt-promise">
          <div className="rt-promise-icon">💰</div>
          <div className="rt-promise-n">Full refund</div>
          <div className="rt-promise-l">Including original shipping</div>
        </div>
        <div className="rt-promise">
          <div className="rt-promise-icon">🔄</div>
          <div className="rt-promise-n">Fast process</div>
          <div className="rt-promise-l">Refunded within 5–7 days</div>
        </div>
      </section>

      {/* HOW TO RETURN */}
      <section className="rt-section">
        <div className="rt-section-inner">
          <div className="rt-section-label">The Process</div>
          <h2>How to <span className="grad">Return</span> an Item</h2>

          <div className="rt-steps">
            <div className="rt-step">
              <div className="rt-step-num">1</div>
              <div>
                <h3>Start your return</h3>
                <p>
                  Go to <Link to="/account">Account → My Orders</Link>, find the order, and click
                  "Request Return". Tell us why you're returning it.
                </p>
              </div>
            </div>
            <div className="rt-step">
              <div className="rt-step-num">2</div>
              <div>
                <h3>Wait for approval</h3>
                <p>The seller reviews your request within 2 business days and approves or contacts you for details.</p>
              </div>
            </div>
            <div className="rt-step">
              <div className="rt-step-num">3</div>
              <div>
                <h3>Ship it back</h3>
                <p>
                  Once approved, pack the item in its original packaging. Use the prepaid return label
                  sent to your email (if provided) or ship with your own carrier.
                </p>
              </div>
            </div>
            <div className="rt-step">
              <div className="rt-step-num">4</div>
              <div>
                <h3>Get your refund</h3>
                <p>After the seller receives and inspects the item, we refund to your original payment method within 5–7 business days.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* POLICY */}
      <section className="rt-section rt-section-alt">
        <div className="rt-section-inner">
          <div className="rt-section-label">Our Policy</div>
          <h2>What You Need to <span className="grad">Know</span></h2>

          <div className="rt-policy-grid">
            <div className="rt-policy rt-policy-ok">
              <h3>✓ Eligible for return</h3>
              <ul>
                <li>Items in original condition with packaging</li>
                <li>Defective or damaged items</li>
                <li>Items that don't match the description</li>
                <li>Most clothing, accessories, and electronics</li>
              </ul>
            </div>
            <div className="rt-policy rt-policy-no">
              <h3>✕ Final sale (not returnable)</h3>
              <ul>
                <li>Food, cosmetics, and consumable items</li>
                <li>Custom or personalized products</li>
                <li>Digital downloads</li>
                <li>Items marked "Final Sale" on product page</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* DAMAGED */}
      <section className="rt-section">
        <div className="rt-section-inner">
          <div className="rt-section-label">Damaged Items</div>
          <h2>Received Something <span className="grad">Damaged?</span></h2>
          <p>
            Open your package on camera if possible. Take photos of damage immediately.
            Contact us within 48 hours and we'll expedite the refund or replacement — no
            return shipping required.
          </p>
          <a href="mailto:support@jeptags.com" className="rt-btn-primary">
            ✉️ Report Damaged Item
          </a>
        </div>
      </section>

      {/* QUESTIONS */}
      <section className="rt-contact">
        <h2>Questions about returns?</h2>
        <p>Our support team is here to help.</p>
        <div className="rt-contact-btns">
          <a href="mailto:support@jeptags.com" className="rt-btn-primary">Email Support</a>
          <Link to="/help" className="rt-btn-secondary">Visit Help Center</Link>
        </div>
      </section>
    </div>
  );
}

const STYLES = `
.rt-root *,.rt-root *::before,.rt-root *::after{margin:0;padding:0;box-sizing:border-box}
.rt-root{
  --cyan:#00E5CC;--cyan-bright:#00FFE0;--teal:#22D97F;
  --navy-deep:#030a14;--navy:#0a1628;--navy-mid:#0F2140;
  font-family:'Sora',sans-serif;background:var(--navy-deep);color:#e0f0f0;min-height:100vh;
}
.rt-root a{color:var(--cyan);text-decoration:none}
.rt-root a:hover{text-decoration:underline}
.grad{background:linear-gradient(135deg,var(--cyan-bright),var(--cyan),var(--teal));-webkit-background-clip:text;-webkit-text-fill-color:transparent;background-clip:text}

/* HERO */
.rt-hero{max-width:860px;margin:0 auto;padding:60px 24px 48px;text-align:center;position:relative}
.rt-back{position:absolute;top:60px;left:24px;display:inline-flex;align-items:center;gap:6px;background:none;border:1px solid rgba(0,229,204,0.15);color:rgba(224,240,240,0.6);font-family:'Sora',sans-serif;font-size:13px;padding:8px 16px;border-radius:8px;cursor:pointer;transition:all .3s}
.rt-back:hover{border-color:var(--cyan);color:var(--cyan)}
.rt-back svg{width:14px;height:14px}
.rt-badge{display:inline-block;padding:8px 20px;border-radius:100px;background:rgba(0,229,204,0.08);border:1px solid rgba(0,229,204,0.15);font-size:12px;font-weight:600;letter-spacing:1.5px;text-transform:uppercase;color:var(--cyan);margin-bottom:20px}
.rt-hero h1{font-size:clamp(36px,5vw,52px);font-weight:800;line-height:1.1;letter-spacing:-1.5px;margin-bottom:14px}
.rt-lede{font-size:17px;color:rgba(224,240,240,0.55);max-width:560px;margin:0 auto;line-height:1.7}

/* PROMISES */
.rt-promises{max-width:860px;margin:0 auto 48px;padding:0 24px;display:grid;grid-template-columns:repeat(3,1fr);gap:16px}
.rt-promise{padding:24px 20px;background:rgba(15,33,64,0.5);border:1px solid rgba(0,229,204,0.06);border-radius:18px;text-align:center}
.rt-promise-icon{font-size:32px;margin-bottom:10px}
.rt-promise-n{font-size:20px;font-weight:800;color:var(--cyan);margin-bottom:4px;letter-spacing:-.3px}
.rt-promise-l{font-size:12px;color:rgba(224,240,240,0.5);text-transform:uppercase;letter-spacing:.8px}

/* SECTION */
.rt-section{padding:48px 24px}
.rt-section-alt{background:rgba(15,33,64,0.3)}
.rt-section-inner{max-width:860px;margin:0 auto}
.rt-section-label{display:inline-block;padding:6px 14px;border-radius:100px;background:rgba(0,229,204,0.06);border:1px solid rgba(0,229,204,0.12);font-size:11px;font-weight:600;letter-spacing:1.5px;text-transform:uppercase;color:var(--cyan);margin-bottom:16px}
.rt-section h2{font-size:clamp(26px,4vw,36px);font-weight:800;line-height:1.2;letter-spacing:-.8px;margin-bottom:20px}
.rt-section p{font-size:16px;line-height:1.75;color:rgba(224,240,240,0.6);margin-bottom:16px;max-width:680px}
.rt-section p:last-child{margin-bottom:0}

/* STEPS */
.rt-steps{display:flex;flex-direction:column;gap:20px;margin-top:28px}
.rt-step{display:flex;gap:20px;align-items:flex-start;padding:24px;background:rgba(3,10,20,0.5);border:1px solid rgba(0,229,204,0.06);border-radius:16px}
.rt-step-num{width:44px;height:44px;border-radius:50%;background:linear-gradient(135deg,var(--cyan-bright),var(--teal));color:var(--navy-deep);font-weight:800;font-size:18px;display:flex;align-items:center;justify-content:center;flex-shrink:0}
.rt-step h3{font-size:17px;font-weight:700;color:#e0f0f0;margin-bottom:8px}
.rt-step p{font-size:14px;line-height:1.7;color:rgba(224,240,240,0.55);margin:0}

/* POLICY */
.rt-policy-grid{display:grid;grid-template-columns:1fr 1fr;gap:20px;margin-top:28px}
.rt-policy{padding:24px;border-radius:16px;border:1px solid}
.rt-policy-ok{background:rgba(34,217,127,0.04);border-color:rgba(34,217,127,0.15)}
.rt-policy-no{background:rgba(239,68,68,0.04);border-color:rgba(239,68,68,0.15)}
.rt-policy h3{font-size:16px;font-weight:700;margin-bottom:14px}
.rt-policy-ok h3{color:var(--teal)}
.rt-policy-no h3{color:#f87171}
.rt-policy ul{list-style:none;padding:0;margin:0;display:flex;flex-direction:column;gap:8px}
.rt-policy li{font-size:14px;line-height:1.55;color:rgba(224,240,240,0.6);padding-left:18px;position:relative}
.rt-policy li::before{content:'•';position:absolute;left:0;color:rgba(224,240,240,0.3)}

/* BUTTONS */
.rt-btn-primary{display:inline-block;padding:14px 28px;border-radius:12px;background:linear-gradient(135deg,var(--cyan-bright),var(--teal));color:var(--navy-deep);font-weight:700;font-size:15px;transition:transform .25s;margin-top:12px}
.rt-btn-primary:hover{transform:translateY(-2px);text-decoration:none}
.rt-btn-secondary{display:inline-block;padding:14px 28px;border-radius:12px;border:1px solid rgba(0,229,204,0.25);background:transparent;color:var(--cyan);font-weight:600;font-size:15px;transition:all .25s}
.rt-btn-secondary:hover{border-color:var(--cyan);background:rgba(0,229,204,0.04);text-decoration:none}

/* CONTACT */
.rt-contact{max-width:860px;margin:0 auto;padding:40px 24px 100px;text-align:center}
.rt-contact h2{font-size:26px;font-weight:800;letter-spacing:-.5px;margin-bottom:10px}
.rt-contact p{font-size:15px;color:rgba(224,240,240,0.5);margin-bottom:24px}
.rt-contact-btns{display:flex;gap:12px;justify-content:center;flex-wrap:wrap}

@media(max-width:700px){
  .rt-back{position:static;margin-bottom:20px}
  .rt-hero{padding:40px 20px 32px}
  .rt-promises{grid-template-columns:1fr;gap:10px}
  .rt-policy-grid{grid-template-columns:1fr}
  .rt-step{flex-direction:column;gap:12px}
}
`;
